import { parseSlaPrimeiraRespostaGroupIdsFromValue } from '../pages/Qualidade/qualityBusinessHours'
import { kindLabel } from '../pages/Qualidade/qualidadeKind'
import { formatTicketNumber, QualityTicket, QualityTicketComment } from '../types/quality'
import { getQualityParameterValueByKey } from './quality'
import { notificationsService } from './notifications'
import { supabase } from '../lib/supabase'

export const QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET = 'grupo_notificacao_ticket'
export const QUALITY_PARAM_SLA_GRUPO_PRIMEIRA_RESPOSTA = 'sla_grupo_usuario_primeira_resposta'

export const QUALITY_AUTO_MESSAGE_FOOTER = 'Mensagem Automática.'

function withAutoMessageFooter(body: string): string {
  const trimmed = body.trimEnd()
  if (trimmed.endsWith(QUALITY_AUTO_MESSAGE_FOOTER)) return trimmed
  return `${trimmed}\n\n${QUALITY_AUTO_MESSAGE_FOOTER}`
}

function ticketNumberSuffix(ticket: Pick<QualityTicket, 'ticket_number'>): string {
  return ticket.ticket_number != null ? ` ${formatTicketNumber(ticket.ticket_number)}` : ''
}

function buildQualityTicketListUrl(ticket: Pick<QualityTicket, 'ticket_number'>): string | null {
  if (ticket.ticket_number == null) return null
  if (typeof window === 'undefined') return null
  return `${window.location.origin}/qualidade/tickets?q=${ticket.ticket_number}`
}

function appendTicketLink(body: string, ticket: Pick<QualityTicket, 'ticket_number'>): string {
  const url = buildQualityTicketListUrl(ticket)
  if (!url) return body
  return `${body.trimEnd()}\n\n🔗 ${url}`
}

async function isStageClosed(stageId: string | null | undefined): Promise<boolean> {
  if (!stageId) return false
  const { data } = await supabase
    .from('quality_ticket_stages')
    .select('is_closed')
    .eq('id', stageId)
    .maybeSingle()
  return !!(data as { is_closed?: boolean } | null)?.is_closed
}

async function loadTicketForNotification(ticketId: string): Promise<QualityTicket | null> {
  const { data, error } = await supabase
    .from('quality_tickets')
    .select('id, title, ticket_number, kind, requester_user_id')
    .eq('id', ticketId)
    .is('deleted_at', null)
    .maybeSingle()
  if (error) {
    console.warn('[qualityTicketNotifications] loadTicketForNotification:', error)
    return null
  }
  return data as QualityTicket | null
}

/**
 * Notifica os grupos configurados em `grupo_notificacao_ticket` quando um ticket é criado.
 * Falhas são apenas logadas (não interrompem o fluxo de criação).
 */
export async function notifyTicketOpenedForConfiguredGroup(ticket: QualityTicket): Promise<void> {
  const raw = await getQualityParameterValueByKey(QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET)
  const groupIds = parseSlaPrimeiraRespostaGroupIdsFromValue(raw)
  if (groupIds.length === 0) return

  const numSuffix = ticketNumberSuffix(ticket)
  const title = `Novo Registro de qualidade${numSuffix}`
  const body = appendTicketLink(`${kindLabel(ticket.kind)}: ${ticket.title}`, ticket)

  const { error } = await notificationsService.sendToGroups({
    title,
    body,
    userGroupIds: groupIds,
  })

  if (error) {
    console.warn('[notifyTicketOpenedForConfiguredGroup]', error)
  }
}

function truncateCommentPreview(content: string, maxLen = 160): string {
  const t = content.trim().replace(/\s+/g, ' ')
  if (t.length <= maxLen) return t
  return `${t.slice(0, maxLen)}…`
}

async function resolveCommentAuthorGroupId(comment: QualityTicketComment): Promise<string | null> {
  const fromJoin = comment.author?.user_group_id
  if (fromJoin != null) return fromJoin
  const { data: profile } = await supabase
    .from('profiles')
    .select('user_group_id')
    .eq('id', comment.user_id)
    .maybeSingle()
  return (profile as { user_group_id?: string | null } | null)?.user_group_id ?? null
}

/**
 * Notifica solicitante e grupos de qualidade a cada comentário no Registro.
 * - Remetente da notificação: autor do comentário (via auth.uid() no momento do envio).
 * - Solicitante não recebe se foi ele quem comentou.
 * - Grupos em `grupo_notificacao_ticket` não recebem se o autor pertence a esse grupo.
 */
export async function notifyOnQualityTicketComment(comment: QualityTicketComment): Promise<void> {
  const ticket = await loadTicketForNotification(comment.ticket_id)
  if (!ticket) return

  const rawGroup = await getQualityParameterValueByKey(QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET)
  const qualityGroupIds = parseSlaPrimeiraRespostaGroupIdsFromValue(rawGroup)
  const authorGroupId = await resolveCommentAuthorGroupId(comment)
  const authorName = (comment.author?.name || '').trim() || 'Usuário'

  const userIds: string[] = []
  if (ticket.requester_user_id && ticket.requester_user_id !== comment.user_id) {
    userIds.push(ticket.requester_user_id)
  }

  const userGroupIds = qualityGroupIds.filter(id => id !== authorGroupId)

  if (userIds.length === 0 && userGroupIds.length === 0) return

  const numSuffix = ticketNumberSuffix(ticket)
  const title = `Novo comentário no Registro${numSuffix}`
  const body = appendTicketLink(
    `${authorName} comentou em "${ticket.title}": ${truncateCommentPreview(comment.content)}`,
    ticket,
  )

  const { error } = await notificationsService.sendToGroups({
    title,
    body,
    userGroupIds: userGroupIds.length > 0 ? userGroupIds : undefined,
    userIds: userIds.length > 0 ? userIds : undefined,
    createdByUserId: comment.user_id,
  })

  if (error) {
    console.warn('[notifyOnQualityTicketComment]', error)
  }
}

/**
 * Notifica o solicitante quando o ticket é movido para etapa de encerramento.
 */
export async function notifyRequesterOnTicketCompleted(ticket: QualityTicket): Promise<void> {
  if (!ticket.requester_user_id) return

  const numSuffix = ticketNumberSuffix(ticket)
  const title = `Registro de qualidade concluído${numSuffix}`
  const body = appendTicketLink(
    withAutoMessageFooter(
      `Seu Registro de qualidade (${kindLabel(ticket.kind)}) "${ticket.title}" foi concluído.`,
    ),
    ticket,
  )

  const { error } = await notificationsService.sendToGroups({
    title,
    body,
    userIds: [ticket.requester_user_id],
  })

  if (error) {
    console.warn('[notifyRequesterOnTicketCompleted]', error)
  }
}

/** Dispara notificação de conclusão se a etapa passou de aberta para encerrada. */
export async function maybeNotifyRequesterOnTicketCompleted(
  ticketId: string,
  previousStageId: string | null | undefined,
  newStageId: string | null | undefined,
): Promise<void> {
  if (!newStageId || newStageId === previousStageId) return

  const [wasClosed, isClosed] = await Promise.all([
    isStageClosed(previousStageId),
    isStageClosed(newStageId),
  ])
  if (wasClosed || !isClosed) return

  const ticket = await loadTicketForNotification(ticketId)
  if (!ticket) return
  await notifyRequesterOnTicketCompleted(ticket)
}
